import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'

// Dashboard data. Neon-direct parts come from the same-origin BFF (`/bff/dashboard/*`, see
// functions/_lib/dashboard.ts); the API-backed parts (tunnel/device health, WAN links, unused devices,
// failing terminals, tunnel-status-all) go through the normal `/api` proxy. v1 loaders set
// shouldRevalidate=false, so staleTime is long and there is no refetch on focus.

const STALE = 5 * 60_000

export type Connectivity = 'online' | 'degraded' | 'offline' | 'unknown'
const connectivity = z.enum(['online', 'degraded', 'offline', 'unknown'])

// ---------- netstatus (BFF) ----------

const cardSchema = z.object({
  status: z.enum(['online', 'degraded', 'offline']),
  count: z.number(),
  pct: z.number(),
})
export type StatusCard = z.infer<typeof cardSchema>

const netstatusSchema = z.object({
  cards: z.array(cardSchema),
  wanCards: z.array(cardSchema),
  wan1Cards: z.array(cardSchema),
  wan2Cards: z.array(cardSchema),
  locations: z.array(
    z.object({
      code: z.string(),
      aptosStoreCode: z.string().nullable(),
      name: z.string(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      connectivity,
      wan: connectivity,
      wan1: connectivity,
      wan2: connectivity,
    }),
  ),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  cachedAt: z.string(),
})
export type NetstatusData = z.infer<typeof netstatusSchema>

export interface NetstatusParams {
  /** Connectivity card filter. */
  status?: 'online' | 'degraded' | 'offline'
  /** WAN card filter; narrow to a slot with `wanInterface`. */
  wanStatus?: 'online' | 'degraded' | 'offline'
  wanInterface?: 'wan1' | 'wan2'
  search?: string
  /** 1-based, 25 per page (server-side). */
  page?: number
}

export function netstatusQueryString(p: NetstatusParams): string {
  const q = new URLSearchParams()
  if (p.status) q.set('status', p.status)
  if (p.wanStatus) q.set('wanStatus', p.wanStatus)
  if (p.wanInterface) q.set('wanInterface', p.wanInterface)
  if (p.search) q.set('search', p.search)
  if (p.page && p.page > 1) q.set('page', String(p.page))
  const s = q.toString()
  return s ? `?${s}` : ''
}

/** Fleet connectivity rollup (online/degraded/offline per store + WAN1/WAN2 rollups), filter + search + paginated. */
export function useNetstatus(params: NetstatusParams = {}) {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'dashboard-netstatus'] : ['dashboard', 'netstatus', params],
    queryFn: (): Promise<NetstatusData> =>
      api.bffGet(`/dashboard/netstatus${netstatusQueryString(params)}`, netstatusSchema),
    enabled: !preview,
    staleTime: STALE,
    ...(preview ? { initialData: preview.dashboardNetstatus, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

// ---------- network ----------

const tunnelMixSchema = z.object({
  total: z.number(),
  cloudflare: z.object({ total: z.number(), up: z.number() }),
  meraki: z.object({ total: z.number(), up: z.number() }),
})
export type TunnelMix = z.infer<typeof tunnelMixSchema>

const bffNetworkSchema = z.object({
  tunnelMix: tunnelMixSchema,
  locationNames: z.record(z.string(), z.string()),
  cachedAt: z.string(),
})

const tunnelHealthSchema = z.object({
  avgUptimePct: z.number(),
  currentlyDown: z.number(),
  trendDelta: z.number(),
  sparkline: z.array(z.number()),
})
const deviceHealthSchema = z.object({ issueCount: z.number(), unreachable: z.number() })
const wanLinksSchema = z.object({
  locationsWithWan: z.number(),
  primary: z.number(),
  secondary: z.number(),
  tertiary: z.number(),
})
const tunnelStatusEntrySchema = z.object({
  tunnelStatus: z.string().optional(),
  deviceStatus: z.string().optional(),
  tunnels: z.array(z.object({ name: z.string(), status: z.string() })).optional(),
})
export type TunnelStatusEntry = z.infer<typeof tunnelStatusEntrySchema>

export type TunnelHealthCard = z.infer<typeof tunnelHealthSchema>
export type DeviceHealthCard = z.infer<typeof deviceHealthSchema>
export type WanLinksCard = z.infer<typeof wanLinksSchema>

export interface AttentionItem {
  storeCode: string
  storeName: string
  status: 'down' | 'degraded'
  vendor: 'cloudflare' | 'meraki'
  detail: string
}

export interface DashboardNetwork {
  tunnelHealth: TunnelHealthCard
  deviceHealth: DeviceHealthCard
  wanLinks: WanLinksCard
  tunnelMix: TunnelMix
  /** Down first, then degraded; stores sorted by code. */
  attention: AttentionItem[]
  range: string
}

/** Pure port of v1 network.data.ts "needs attention" derivation. Only org stores (keys of `names`) are listed. */
export function buildAttention(all: Record<string, TunnelStatusEntry>, names: Record<string, string>): AttentionItem[] {
  const out: AttentionItem[] = []
  for (const [code, entry] of Object.entries(all)) {
    if (!(code in names)) continue
    const tunnels = entry.tunnels
    const vendor: 'cloudflare' | 'meraki' = Array.isArray(tunnels) ? 'cloudflare' : 'meraki'
    let status: 'down' | 'degraded' | null = null
    let detail = ''
    if (tunnels) {
      const up = tunnels.filter((t) => t.status?.toUpperCase() === 'UP').length
      if (up === 0) {
        status = 'down'
        detail = `0/${tunnels.length} tunnels up`
      } else if (up < tunnels.length) {
        status = 'degraded'
        detail = `${up}/${tunnels.length} tunnels up`
      }
    } else if (entry.tunnelStatus?.toUpperCase() === 'DOWN') {
      status = 'down'
      detail = 'Tunnel down'
    } else if (entry.deviceStatus?.toLowerCase() !== 'online') {
      status = 'degraded'
      detail = `Device ${entry.deviceStatus ?? 'unknown'}`
    }
    if (status) out.push({ storeCode: code, storeName: names[code] ?? code, status, vendor, detail })
  }
  return out.sort((a, b) =>
    a.status !== b.status ? (a.status === 'down' ? -1 : 1) : a.storeCode.localeCompare(b.storeCode),
  )
}

const dataEnvelope = <T extends z.ZodType>(s: T) => z.object({ data: s })

/** Best-effort API read: a failing card degrades to its fallback instead of failing the whole dashboard (as v1). */
const soft = <T>(p: Promise<{ data: T }>, fallback: T): Promise<T> =>
  p.then(
    (r) => r.data,
    () => fallback,
  )

/** Tunnel/device health, WAN links, vendor mix and the "needs attention" list. `range`: 1d|7d|30d (default 30d). */
export function useDashboardNetwork(range = '30d') {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'dashboard-network'] : ['dashboard', 'network', range],
    queryFn: async (): Promise<DashboardNetwork> => {
      const r = encodeURIComponent(range)
      const [bff, tunnelHealth, deviceHealth, wanLinks, all] = await Promise.all([
        api.bffGet('/dashboard/network', bffNetworkSchema),
        soft(api.get(`/v1/dashboard/tunnel-health?range=${r}`, dataEnvelope(tunnelHealthSchema)), {
          avgUptimePct: 0,
          currentlyDown: 0,
          trendDelta: 0,
          sparkline: [],
        }),
        soft(api.get(`/v1/dashboard/device-health?range=${r}`, dataEnvelope(deviceHealthSchema)), {
          issueCount: 0,
          unreachable: 0,
        }),
        soft(api.get('/v1/dashboard/wan-links', dataEnvelope(wanLinksSchema)), {
          locationsWithWan: 0,
          primary: 0,
          secondary: 0,
          tertiary: 0,
        }),
        soft(
          api.get('/v1/tunnel-status-all', dataEnvelope(z.record(z.string(), tunnelStatusEntrySchema))),
          {} as Record<string, TunnelStatusEntry>,
        ),
      ])
      return {
        tunnelHealth,
        deviceHealth,
        wanLinks,
        tunnelMix: bff.tunnelMix,
        attention: buildAttention(all, bff.locationNames),
        range,
      }
    },
    enabled: !preview,
    staleTime: STALE,
    ...(preview ? { initialData: preview.dashboardNetwork, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

// ---------- pos ----------

const bffPosSchema = z.object({
  terminalStatus: z.object({ total: z.number(), online: z.number(), offline: z.number() }),
  staleCheckIns: z.object({ count: z.number(), affectedLocations: z.number() }),
  paymentMethods: z.object({ locationsWithAdyen: z.number(), active: z.number(), inactive: z.number() }),
  range: z.string(),
  days: z.number(),
  cachedAt: z.string(),
})

const failingTerminalSchema = z.object({
  terminalId: z.string(),
  serialNumber: z.string(),
  model: z.string(),
  locationCode: z.string().nullable(),
  aptosStoreCode: z.string().nullable(),
  locationName: z.string().nullable(),
  banner: z.string().nullable(),
  workstationId: z.string().nullable(),
  adyenStoreId: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
  lastTransactionAt: z.string().nullable(),
  reason: z.enum(['not-checking-in', 'no-activity-24h']),
})
export type AdyenFailingDevice = z.infer<typeof failingTerminalSchema>

export interface DashboardPos {
  terminalStatus: { total: number; online: number; offline: number }
  staleCheckIns: { count: number; affectedLocations: number }
  unusedWorkstations: { count: number; days: number }
  paymentMethods: { locationsWithAdyen: number; active: number; inactive: number }
  failingTerminals: AdyenFailingDevice[]
  range: string
}

/** POS health. `range`: 1d|3d|7d|30d (default 1d). Unknown ranges fall back to 1d server-side. */
export function useDashboardPos(range = '1d') {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'dashboard-pos'] : ['dashboard', 'pos', range],
    queryFn: async (): Promise<DashboardPos> => {
      const bff = await api.bffGet(`/dashboard/pos?range=${encodeURIComponent(range)}`, bffPosSchema)
      const hours = bff.days * 24
      const [unused, failing] = await Promise.all([
        soft(
          api.get(
            `/v1/dashboard/unused-devices?days=${bff.days}`,
            dataEnvelope(z.object({ devices: z.array(z.unknown()) })),
          ),
          { devices: [] as unknown[] },
        ),
        soft(
          api.get(`/v1/monitoring/devices?hours=${hours}`, dataEnvelope(z.array(failingTerminalSchema))),
          [] as AdyenFailingDevice[],
        ),
      ])
      return {
        terminalStatus: bff.terminalStatus,
        staleCheckIns: bff.staleCheckIns,
        unusedWorkstations: { count: unused.devices.length, days: bff.days },
        paymentMethods: bff.paymentMethods,
        failingTerminals: failing,
        range: bff.range,
      }
    },
    enabled: !preview,
    staleTime: STALE,
    ...(preview ? { initialData: preview.dashboardPos, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}
